import { useEffect, useState } from "react";
import { ApiError } from "@/lib/apiClient";
import { toast } from "@/lib/toast";
import { useCurrentUser } from "@/lib/auth";
import {
  closeAnfrage,
  getAnfrage,
  getAnfrageMailCandidateTargets,
  getAnfrageMailCandidates,
  getAnfrageMailContentDetail,
  patchAnfrageMailContent,
  reopenAnfrage,
  sendAnfrageGroupMail,
  sendAnfrageMail,
  type AnfrageDetail,
  type AnfrageMailCandidate,
  type AnfrageMailCandidateTarget,
  type AnfrageMailContentDetail,
  type AnfrageMailReviewItem,
} from "@/lib/anfragen";
import { AnfrageDetailDialog } from "@/pages/anfragen/AnfrageDetailDialog";
import { ConfirmMailCandidateDialog } from "@/pages/anfragen/ConfirmMailCandidateDialog";
import { ConvertToOrderDialog } from "@/pages/anfragen/ConvertToOrderDialog";
import { MailReadDialog } from "@/pages/anfragen/MailReadDialog";
import { TaMailReviewDialog, type TaMailReviewResult } from "@/pages/anfragen/TaMailReviewDialog";
import { buildAnfrageMailReviewItems, buildMailtoHref } from "@/pages/anfragen/anfrageWizardUtils";

function detailAuftragIds(row: AnfrageDetail | null): number[] {
  if (!row?.positionen?.length) return [];
  return [...new Set(row.positionen.map((p) => p.auftrag_id).filter((id) => Number(id) > 0))];
}

export type AnfrageDetailStartAction = "convert" | "send" | null;

export function AnfrageDetailHost({
  open,
  detail,
  loading,
  mailCounts,
  startAction = null,
  onStartActionConsumed,
  onClose,
  onReloadDetail,
  onChanged,
}: {
  open: boolean;
  detail: AnfrageDetail | null;
  loading: boolean;
  mailCounts: Record<string, number>;
  /** Direkt nach dem Laden eine Aktion starten (z. B. „Jetzt versenden“ aus der Liste). */
  startAction?: AnfrageDetailStartAction;
  onStartActionConsumed?: () => void;
  onClose: () => void;
  onReloadDetail: () => void | Promise<void>;
  onChanged: () => void | Promise<void>;
}) {
  const { user } = useCurrentUser();
  const isEntra = String(user?.auth_provider || "").toLowerCase() === "entra";

  const [convertOpen, setConvertOpen] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [mailReadOpen, setMailReadOpen] = useState(false);
  const [mailReadDetail, setMailReadDetail] = useState<AnfrageMailContentDetail | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmCandidate, setConfirmCandidate] = useState<AnfrageMailCandidate | null>(null);
  const [confirmTargets, setConfirmTargets] = useState<AnfrageMailCandidateTarget[]>([]);
  const [detailMailReview, setDetailMailReview] = useState<{
    open: boolean;
    items: AnfrageMailReviewItem[];
    groupId: number | null;
    auftragIds: number[];
  }>({ open: false, items: [], groupId: null, auftragIds: [] });

  useEffect(() => {
    if (!open || !startAction || !detail) return;
    if (startAction === "convert") {
      setConvertOpen(true);
    } else if (startAction === "send") {
      void handleSendDetail();
    }
    onStartActionConsumed?.();
    // handleSendDetail ist eine stabile Funktion innerhalb dieser Komponente.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, startAction, detail, onStartActionConsumed]);

  async function handleClose() {
    if (!detail?.id || statusBusy) return;
    setStatusBusy(true);
    try {
      await closeAnfrage(detail.id);
      toast.success("Anfrage als erledigt markiert.");
      await onReloadDetail();
      await onChanged();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Anfrage konnte nicht abgeschlossen werden.");
    } finally {
      setStatusBusy(false);
    }
  }

  async function handleReopen() {
    if (!detail?.id || statusBusy) return;
    setStatusBusy(true);
    try {
      await reopenAnfrage(detail.id);
      toast.success("Anfrage wieder geöffnet.");
      await onReloadDetail();
      await onChanged();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Anfrage konnte nicht geöffnet werden.");
    } finally {
      setStatusBusy(false);
    }
  }

  async function handleOpenMailRead(ref: { ref_type: string; ref_id?: number | string | null }) {
    if (!detail?.id || !ref.ref_type) return;
    try {
      const content = await getAnfrageMailContentDetail(detail.id, ref);
      setMailReadDetail(content);
      setMailReadOpen(true);
    } catch {
      toast.error("Mail konnte nicht geladen werden.");
    }
  }

  async function handleConfirmCandidateOpen(candidateId: number) {
    if (!detail?.id) return;
    try {
      const [targets, data] = await Promise.all([
        getAnfrageMailCandidateTargets(detail.id),
        getAnfrageMailCandidates(detail.id),
      ]);
      setConfirmTargets(targets);
      const found = (data.candidates || []).find((c) => Number(c.id) === Number(candidateId));
      if (!found) {
        toast.warning("Vorschlag nicht mehr verfügbar.");
        return;
      }
      setConfirmCandidate(found);
      setConfirmOpen(true);
    } catch {
      toast.error("Vorschlag konnte nicht geladen werden.");
    }
  }

  async function openDetailMailReview(
    items: Array<{
      anfrage_id: number;
      to: string;
      supplier_label?: string;
      subject: string;
      body: string;
    }>,
    groupId: number | null,
    auftragIds: number[],
  ) {
    setDetailMailReview({
      open: true,
      items: await buildAnfrageMailReviewItems(items),
      groupId,
      auftragIds,
    });
  }

  async function handleSendDetail() {
    if (!detail) return;
    const auftragIds = detailAuftragIds(detail);

    if (detail.anfrage_group_id) {
      const ids = [detail.id, ...(detail.group_siblings || []).map((s) => s.id)];
      const loaded: AnfrageDetail[] = [];
      for (const id of ids) {
        try {
          loaded.push(await getAnfrage(id));
        } catch {
          /* skip */
        }
      }
      if (!loaded.length) {
        toast.error("Gruppendaten konnten nicht geladen werden.");
        return;
      }
      const allAuftragIds = [...new Set(loaded.flatMap((x) => detailAuftragIds(x)))];
      await openDetailMailReview(
        loaded.map((x) => ({
          anfrage_id: x.id,
          to: x.supplier_email || "",
          supplier_label: x.supplier_name || "",
          subject: x.subject || "",
          body: x.body || "",
        })),
        detail.anfrage_group_id,
        allAuftragIds,
      );
      return;
    }

    await openDetailMailReview(
      [
        {
          anfrage_id: detail.id,
          to: detail.supplier_email || "",
          supplier_label: detail.supplier_name || "",
          subject: detail.subject || "",
          body: detail.body || "",
        },
      ],
      null,
      auftragIds,
    );
  }

  async function handleDetailMailReviewResult(result: TaMailReviewResult) {
    if (result.action === "cancel") {
      setDetailMailReview({ open: false, items: [], groupId: null, auftragIds: [] });
      return;
    }
    const items = result.items;
    if (!items.length) return;

    const reviewSnapshot = detailMailReview;
    setDetailMailReview({ open: false, items: [], groupId: null, auftragIds: [] });

    try {
      if (result.action === "mailto") {
        if (result.sharepointItemIds.length || result.attachments.length) {
          toast.warning(
            "SharePoint- und lokale Anhänge bitte manuell im Mailprogramm hinzufügen oder Direktversand nutzen.",
          );
        }
        const it = items[0];
        if (!it) return;
        await patchAnfrageMailContent(it.anfrage_id, {
          subject: it.subject,
          body: it.bodyPlain,
          mark_sent: true,
        });
        const href = buildMailtoHref(it.to, it.subject, it.bodyPlainMailto || it.bodyPlain);
        const a = document.createElement("a");
        a.href = href;
        a.rel = "noopener";
        a.style.display = "none";
        document.body.appendChild(a);
        a.click();
        a.remove();
        toast.success(items.length > 1 ? "Erste Mail ins Mailprogramm übernommen." : "Mailprogramm geöffnet.");
      } else if (result.action === "graph") {
        const attachPayload = result.attachments.length ? result.attachments : undefined;
        const spIds = result.sharepointItemIds.length ? result.sharepointItemIds : undefined;
        if (reviewSnapshot.groupId) {
          const messages = items.map((it) => ({
            anfrage_id: it.anfrage_id,
            to: it.to,
            subject: it.subject,
            body: it.bodyPlain,
            html_body: it.bodyHtml,
          }));
          const res = await sendAnfrageGroupMail(reviewSnapshot.groupId, {
            messages,
            sharepoint_item_ids: spIds,
            attachments: attachPayload,
          });
          const ok = (res.results || []).filter((x) => x.ok).length;
          const tot = (res.results || []).length;
          toast.success(`Mail an ${ok}/${tot} Firmen gesendet.`);
        } else if (items.length > 1) {
          let ok = 0;
          for (const it of items) {
            await sendAnfrageMail(it.anfrage_id, {
              to: it.to,
              subject: it.subject,
              body: it.bodyPlain,
              html_body: it.bodyHtml,
              sharepoint_item_ids: spIds,
              attachments: attachPayload,
            });
            ok += 1;
          }
          toast.success(`${ok} Mails gesendet.`);
        } else {
          const it = items[0];
          if (!it) return;
          await sendAnfrageMail(it.anfrage_id, {
            to: it.to,
            subject: it.subject,
            body: it.bodyPlain,
            html_body: it.bodyHtml,
            sharepoint_item_ids: spIds,
            attachments: attachPayload,
          });
          toast.success("Mail gesendet.");
        }
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 412) {
        toast.warning("Microsoft-Anmeldung muss erneuert werden.");
      } else {
        toast.error(err instanceof Error ? err.message : "Versand fehlgeschlagen.");
      }
      setDetailMailReview({
        open: true,
        items,
        groupId: reviewSnapshot.groupId,
        auftragIds: reviewSnapshot.auftragIds,
      });
      return;
    }

    await onReloadDetail();
    await onChanged();
  }

  return (
    <>
      <AnfrageDetailDialog
        open={open}
        detail={detail}
        loading={loading}
        mailCounts={mailCounts}
        onClose={onClose}
        onConvert={() => setConvertOpen(true)}
        onRefresh={() => void onChanged()}
        onReloadDetail={() => {
          void onReloadDetail();
        }}
        onSend={() => void handleSendDetail()}
        onMarkDone={() => void handleClose()}
        onReopen={() => void handleReopen()}
        statusBusy={statusBusy}
        onOpenMailRead={(ref) => void handleOpenMailRead(ref)}
        onConfirmMailCandidate={(id) => void handleConfirmCandidateOpen(id)}
      />

      <ConvertToOrderDialog
        open={convertOpen}
        detail={detail}
        onClose={() => setConvertOpen(false)}
        onConverted={async () => {
          setConvertOpen(false);
          onClose();
          await onChanged();
        }}
      />

      <MailReadDialog
        open={mailReadOpen}
        detail={mailReadDetail}
        onClose={() => {
          setMailReadOpen(false);
          setMailReadDetail(null);
        }}
      />

      <ConfirmMailCandidateDialog
        open={confirmOpen}
        anfrageId={detail?.id ?? null}
        candidate={confirmCandidate}
        targets={confirmTargets}
        onClose={() => {
          setConfirmOpen(false);
          setConfirmCandidate(null);
        }}
        onConfirmed={async () => {
          setConfirmOpen(false);
          setConfirmCandidate(null);
          await onReloadDetail();
          await onChanged();
          toast.success("Mail abgelegt und Anfrage verknüpft.");
        }}
      />

      <TaMailReviewDialog
        open={detailMailReview.open}
        items={detailMailReview.items}
        auftragIds={detailMailReview.auftragIds}
        isEntra={isEntra}
        onResolve={(result) => void handleDetailMailReviewResult(result)}
      />
    </>
  );
}
